import React from "react";
import classnames from "classnames";
import { DispatchProp } from "react-redux";
import SignaturePad, { PointGroup } from "signature_pad";
import { useTheme } from "react-jss";
import { ControlProps } from "./BaseControl";
import useDimensionContext from "../../hooks/useDimensionContext";
import { getSettingString, getSettingBoolean } from "../../models/utilities/settingUtilities";
import useStyles from "./SignatureControl.styles";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import Button from "../shared/Button";
import { ButtonChoice } from "../shared/ButtonChoice";
import useTranslatable from "../../hooks/useTranslatable";
import { useMentions } from "../../hooks/useMentions";
import { useValidation, useAnswerRequiredValidator } from "../../hooks/useValidation";
import { VariableDataType } from "../../models/enums/VariableDataType";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { translate } from "../../models/utilities/translationUtilities";
import { CloseIcon } from "../shared/ModernFileUploadIcons";
import { SignatureHintIcon, SignatureUndoIcon } from "../shared/ModernSignatureIcons";
import {
    MODERN_SIGNATURE_CLEAR_ICON_SIZE_PX,
    SignatureSizeType,
    useModernSignatureStyles,
} from "../../theme/signatureThemeTokens";

interface Props extends ControlProps, DispatchProp {}

type SizeType = SignatureSizeType;

function resizeData(data: PointGroup[], scaleX: number, scaleY?: number): PointGroup[] {
    const yScale = scaleY ?? scaleX;
    return data.map((pointGroup) => ({
        ...pointGroup,
        points: pointGroup.points.map((point) => ({
            ...point,
            x: point.x * scaleX,
            y: point.y * yScale,
        })),
    }));
}

function maxSize(size: SizeType) {
    switch (size) {
        case "Small":
            return 300;
        case "Medium":
            return 500;
        case "Full":
            return 1000;
        default:
            return 800;
    }
}

function onChoiceKeyDown(event, onToggleNoAnswer) {
    if (event.target !== event.currentTarget) {
        return;
    }
    if (event.key === " ") {
        onToggleNoAnswer();
        event.preventDefault();
    }
}

function configureCanvasForDpi(
    canvas: HTMLCanvasElement,
    cssWidth: number,
    cssHeight: number,
): void {
    const dpr = window.devicePixelRatio || 1;
    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.scale(dpr, dpr);
    }
}

export function SignatureControl(props: Props) {
    const { question, questionnaire, language, validateSignal } = props;

    const displayNoAnswer = getSettingBoolean(question, "DisplayNoAnswer");
    const noAnswerValue = getSettingString(question, "NoAnswerValue");
    const noAnswerLabel = useTranslatable(question, "NoAnswerLabel");

    const variable = question.variables[0];
    const canvasRef = React.useRef<HTMLCanvasElement>();
    const signatureRef = React.useRef<SignaturePad>();
    const { innerWidth } = useDimensionContext();
    const [mentions, setMentions] = useMentions(variable.name);
    const mentionValue = mentions[0] && mentions[0].value;
    const [hasLines, setHasLines] = React.useState(false);
    const [initialValue, setInitialValue] = React.useState(
        mentionValue !== noAnswerValue ? mentionValue : undefined,
    );
    const [statusAnnouncement, setStatusAnnouncement] = React.useState("");
    // Clear the live region before setting the new text so successive identical messages
    // (e.g. clearing the canvas twice) still trigger a DOM mutation and a fresh aria-live announce.
    const announceTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
    const announce = React.useCallback((text: string) => {
        if (announceTimeoutRef.current) {
            clearTimeout(announceTimeoutRef.current);
        }
        setStatusAnnouncement("");
        announceTimeoutRef.current = setTimeout(() => setStatusAnnouncement(text), 50);
    }, []);
    React.useEffect(
        () => () => {
            if (announceTimeoutRef.current) {
                clearTimeout(announceTimeoutRef.current);
            }
        },
        [],
    );

    // Mirror hasLines into a ref so the canvas event handler (registered once inside useEffect)
    // can read the current value without re-binding on every state change.
    const hasLinesRef = React.useRef(false);
    React.useEffect(() => {
        hasLinesRef.current = hasLines;
    }, [hasLines]);

    const drawingAreaLabel =
        translate(questionnaire, "SignatureDrawingArea", language) || "Signature drawing area";
    const instructionText =
        translate(questionnaire, "SignatureInstruction", language) ||
        "Draw your signature in the box below";
    const undoLabel =
        translate(questionnaire, "SignatureUndoButton", language) || "Undo last stroke";
    const clearLabel =
        translate(questionnaire, "SignatureClearButton", language) || "Clear signature";
    const clearedAnnouncement =
        translate(questionnaire, "SignatureCleared", language) || "Signature cleared";
    const undoneAnnouncement =
        translate(questionnaire, "SignatureUndone", language) || "Last stroke removed";
    const savedAnnouncement =
        translate(questionnaire, "SignatureSaved", language) || "Signature saved";

    // Ref so the canvas onEnd handler (registered once inside useEffect) always sees
    // the latest translated string when language toggles mid-session.
    const savedAnnouncementRef = React.useRef(savedAnnouncement);
    React.useEffect(() => {
        savedAnnouncementRef.current = savedAnnouncement;
    }, [savedAnnouncement]);

    const theme = useTheme();
    const isModern = useModernSignatureStyles(theme);
    const ratio = 21 / 9;
    const sizeSetting = getSettingString(question, "Size") as SizeType;
    const penColor = getSettingString(question, "PenColor", "#000000");
    const signatureWidth = Math.min(innerWidth - 2, maxSize(sizeSetting));
    const classes = useStyles({
        theme,
        signatureWidth,
        ratio,
    } as {
        theme: any;
        signatureWidth: number;
        ratio: number;
    });

    const isSvg = variable.dataType === VariableDataType.Base64;
    const isSigned = hasLines || Boolean(initialValue);

    // Get the standard answer required validator (must be called at top level)
    const answerRequiredValidator = useAnswerRequiredValidator(question, variable);

    // Custom validator that ensures signature is saved before validation
    const signatureValidator = React.useCallback(
        (options: ValidationOptionsModel): ValidationResultModel => {
            // If signature pad exists and has content, save it before validation
            if (signatureRef.current && !signatureRef.current.isEmpty()) {
                const dataURL = signatureRef.current.toDataURL(isSvg ? "image/svg+xml" : null);
                // Only save if it's different from the current mention value
                if (dataURL && mentionValue !== dataURL) {
                    setMentions((b) => b.set(dataURL));
                    setHasLines(true);
                }
            }
            // Then run the standard answer required validator
            // The validator will check mentions, and if we just saved it above,
            // the state should be updated (Redux updates are synchronous within dispatch)
            return answerRequiredValidator(options);
        },
        [answerRequiredValidator, isSvg, mentionValue, setMentions],
    );

    useValidation(validateSignal, signatureValidator);

    const handleClear = React.useCallback(() => {
        setMentions((b) => b.clear());
        setInitialValue(undefined);
        setHasLines(false);
        if (signatureRef.current) {
            signatureRef.current.clear();
        }
        announce(clearedAnnouncement);
    }, [announce, clearedAnnouncement, setMentions]);

    const handleUndo = React.useCallback(() => {
        if (!signatureRef.current) {
            return;
        }
        const data = signatureRef.current.toData();
        signatureRef.current.clear();
        signatureRef.current.fromData(data.slice(0, -1));
        if (data.length > 1) {
            setMentions((b) =>
                b.set(signatureRef.current.toDataURL(isSvg ? "image/svg+xml" : null)),
            );
        } else {
            setMentions((m) => m.clear());
            setHasLines(false);
        }
        announce(undoneAnnouncement);
    }, [announce, isSvg, setMentions, undoneAnnouncement]);

    React.useEffect(() => {
        if (initialValue) {
            return undefined;
        }
        let data;
        if (signatureRef.current) {
            data = signatureRef.current.toData();
        }
        const canvas = canvasRef.current;
        if (!canvas) {
            return undefined;
        }
        const prevCanvasWidth = canvas.width;
        const prevCanvasHeight = canvas.height;
        const cssWidth = signatureWidth;
        const cssHeight = cssWidth / ratio;

        if (isModern) {
            configureCanvasForDpi(canvas, cssWidth, cssHeight);
        } else {
            canvas.width = cssWidth;
            canvas.height = cssHeight;
        }

        signatureRef.current = new SignaturePad(canvas, {
            penColor,
            minWidth: signatureWidth * 0.002,
            maxWidth: signatureWidth * 0.006,
            minDistance: signatureWidth * 0.006,
        });
        // Register the stroke-end listener after creating the instance.
        // signature_pad@4.x dispatches "endStroke" (not "end") — earlier code listened to a non-existent event.
        signatureRef.current.addEventListener("endStroke", () => {
            if (!hasLinesRef.current) {
                announce(savedAnnouncementRef.current);
            }
            setHasLines(true);
            setMentions((b) =>
                b.set(signatureRef.current.toDataURL(isSvg ? "image/svg+xml" : null)),
            );
        });
        if (data) {
            const scaleX = canvas.width / prevCanvasWidth;
            const scaleY = canvas.height / prevCanvasHeight;
            data = resizeData(data, scaleX, scaleY);
            signatureRef.current.fromData(data);
        }

        return () => {
            signatureRef.current.off();
        };
    }, [
        initialValue,
        isModern,
        isSvg,
        penColor,
        ratio,
        setMentions,
        signatureWidth,
    ]);

    const onToggleNoAnswer = React.useCallback(() => {
        signatureRef.current.clear();
        if (mentionValue === noAnswerValue) {
            setMentions((b) => b.clear());
        } else {
            setMentions((b) => b.set(noAnswerValue));
        }
        setHasLines(false);
        announce(clearedAnnouncement);
    }, [mentionValue, noAnswerValue, setMentions, clearedAnnouncement, announce]);

    return (
        <div role="group" aria-labelledby={`${question.elementId}_LABEL`}>
            {!isModern && (
                <p className={classes.instruction} aria-hidden="true">
                    {instructionText}
                </p>
            )}
            <div className={classes.container}>
                <div className={classes.signatureArea}>
                    {!isModern && (
                        <>
                            <div className={classes.topLeftCorner} />
                            <div className={classes.topRightCorner} />
                            <div className={classes.bottomLeftCorner} />
                            <div className={classes.bottomRightCorner} />
                        </>
                    )}
                    {hasLines &&
                        (isModern ? (
                            <Button
                                buttonType="SIGNATURE_UNDO"
                                className={classes.undoButtonModern}
                                customIcon={
                                    <SignatureUndoIcon size={MODERN_SIGNATURE_CLEAR_ICON_SIZE_PX} />
                                }
                                text={undoLabel}
                                displayText={false}
                                onClick={handleUndo}
                            />
                        ) : (
                            <div className={classes.undoButton}>
                                <Button
                                    icon={["fas", "undo"]}
                                    text={undoLabel}
                                    displayText={false}
                                    size={signatureWidth * 0.03}
                                    onClick={handleUndo}
                                />
                            </div>
                        ))}
                    {(!isModern || isSigned) &&
                        (isModern ? (
                            <Button
                                buttonType="SIGNATURE_CLEAR"
                                className={classes.clearButtonModern}
                                customIcon={
                                    <CloseIcon size={MODERN_SIGNATURE_CLEAR_ICON_SIZE_PX} />
                                }
                                text={clearLabel}
                                displayText={false}
                                onClick={handleClear}
                            />
                        ) : (
                            <div className={classes.clearButton}>
                                <Button
                                    icon={["fas", "times"]}
                                    text={clearLabel}
                                    displayText={false}
                                    size={signatureWidth * 0.03}
                                    onClick={handleClear}
                                />
                            </div>
                        ))}
                    {!isModern && (
                        <div className={classes.signatureLine}>
                            <FontAwesomeIcon icon="signature" fixedWidth />
                        </div>
                    )}
                    {isModern && (
                        <div className={classes.hintOverlay}>
                            <div
                                className={classnames(
                                    classes.hintRow,
                                    isSigned && classes.hintHidden,
                                )}
                                aria-hidden="true"
                            >
                                <SignatureHintIcon />
                            </div>
                            <div className={classes.baselineModern} />
                        </div>
                    )}
                    {!initialValue && (
                        <canvas
                            className={classes.canvas}
                            ref={canvasRef}
                            role="img"
                            aria-label={drawingAreaLabel}
                        />
                    )}
                    {initialValue && (
                        <img
                            className={classes.image}
                            src={initialValue}
                            alt={drawingAreaLabel}
                        />
                    )}
                </div>
            </div>
            {displayNoAnswer && (
                <ButtonChoice
                    type="checkbox"
                    onKeyDown={(event) => onChoiceKeyDown(event, onToggleNoAnswer)}
                    onSelect={onToggleNoAnswer}
                    selected={mentionValue === noAnswerValue}
                    label={noAnswerLabel}
                />
            )}
            <span
                className={classes.srOnly}
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                {statusAnnouncement}
            </span>
        </div>
    );
}
